/**
 * NewProductForm.tsx — register a new product code (Story N5, wireframe state 10).
 *
 * Same `Field`/error-banner shell as `NewLeadForm`: labelled fields, inline
 * `role="alert"` errors, `aria-describedby` wiring. `collateral_model` /
 * `pricing_model` are `<select>`s sourced from the contract-shared
 * `COLLATERAL_MODELS`/`PRICING_MODELS` const enums (N5 AC2) — never free
 * text, so an out-of-domain value can never reach the API from this form.
 *
 * Write-error posture (N5 AC3, the load-bearing one): a 409 (duplicate
 * `product_code`, the PK) renders the fixed operator copy "this product code
 * already exists" — never the BFF's raw `detail` string, and never collapsed
 * into the generic "invalid" banner. No delete/retire control lives here
 * (N5 AC4) — retiring is the catalogue row's `is_active` toggle, a separate
 * action this form never offers.
 */

import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, UnauthorizedError, type ApiClient } from '../../api/client';
import {
  COLLATERAL_MODELS,
  PRICING_MODELS,
  type CollateralModel,
  type PricingModel,
  type ProductConfig,
} from '../../api/types';
import { Field, inputClass } from '../../components/Field';
import floating from '../../lib/floating';

export interface NewProductFormProps {
  apiClient: ApiClient;
  onCreated: (product: ProductConfig) => void;
  onCancel: () => void;
}

interface FieldErrors {
  productCode?: string;
  displayName?: string;
  collateralModel?: string;
  pricingModel?: string;
}

type WriteError =
  { kind: 'invalid' } | { kind: 'duplicate' } | { kind: 'unavailable' } | { kind: 'unauthorized' };
const WriteErrorBanner = ({ error }: { error: WriteError }) => {
  const { t } = useTranslation();
  const baseClass =
    'rounded-lg bg-status-down/15 px-4 py-2 text-meta text-status-down ring-1 ring-inset ring-status-down/40';

  if (error.kind === 'duplicate') {
    return (
      <p role="alert" data-testid="new-product-error-duplicate" className={baseClass}>
        {t('products.newProduct.errors.duplicate')}
      </p>
    );
  }
  if (error.kind === 'invalid') {
    return (
      <p role="alert" data-testid="new-product-error-invalid" className={baseClass}>
        {t('products.newProduct.errors.invalid')}
      </p>
    );
  }
  if (error.kind === 'unauthorized') {
    return (
      <p role="alert" data-testid="new-product-error-unauthorized" className={baseClass}>
        {t('products.newProduct.errors.unauthorized')}
      </p>
    );
  }
  return (
    <p role="alert" data-testid="new-product-error-unavailable" className={baseClass}>
      {t('products.newProduct.errors.unavailable')}
    </p>
  );
};

export const NewProductForm = ({ apiClient, onCreated, onCancel }: NewProductFormProps) => {
  const { t } = useTranslation();
  const formId = useId();

  const [productCode, setProductCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [collateralModel, setCollateralModel] = useState<CollateralModel | ''>('');
  const [pricingModel, setPricingModel] = useState<PricingModel | ''>('');

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [writeError, setWriteError] = useState<WriteError | null>(null);

  const validateProductCode = (value: string) =>
    value.trim() === '' ? t('products.newProduct.errors.productCodeRequired') : undefined;
  const validateDisplayName = (value: string) =>
    value.trim() === '' ? t('products.newProduct.errors.displayNameRequired') : undefined;
  const validateCollateralModel = (value: CollateralModel | '') =>
    value === '' ? t('products.newProduct.errors.collateralModelRequired') : undefined;
  const validatePricingModel = (value: PricingModel | '') =>
    value === '' ? t('products.newProduct.errors.pricingModelRequired') : undefined;

  const validate = (): FieldErrors => {
    const errors: FieldErrors = {};
    const productCodeError = validateProductCode(productCode);
    if (productCodeError != null) errors.productCode = productCodeError;
    const displayNameError = validateDisplayName(displayName);
    if (displayNameError != null) errors.displayName = displayNameError;
    const collateralModelError = validateCollateralModel(collateralModel);
    if (collateralModelError != null) errors.collateralModel = collateralModelError;
    const pricingModelError = validatePricingModel(pricingModel);
    if (pricingModelError != null) errors.pricingModel = pricingModelError;
    return errors;
  };

  const revalidate = (field: keyof FieldErrors, error: string | undefined) => {
    setFieldErrors((prev) => (prev[field] == null ? prev : { ...prev, [field]: error }));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    setWriteError(null);
    try {
      const product = await apiClient.createProduct({
        product_code: productCode.trim(),
        display_name: displayName.trim(),
        collateral_model: collateralModel as CollateralModel,
        pricing_model: pricingModel as PricingModel,
      });
      onCreated(product);
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setWriteError({ kind: 'duplicate' });
      } else if (error instanceof ApiError && error.status === 422) {
        setWriteError({ kind: 'invalid' });
      } else if (error instanceof UnauthorizedError) {
        setWriteError({ kind: 'unauthorized' });
      } else {
        setWriteError({ kind: 'unavailable' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      aria-labelledby={`${formId}-heading`}
      data-testid="new-product-form"
      className="flex flex-col gap-3 rounded-xl bg-surface-2 p-4 ring-1 ring-inset ring-ink-3/10"
      onSubmit={(event) => floating(handleSubmit(event))}
      noValidate
    >
      <h2 id={`${formId}-heading`} className="text-h3 text-ink-1">
        {t('products.newProduct.heading')}
      </h2>

      <Field
        id={`${formId}-code`}
        label={t('products.newProduct.productCode')}
        hint={t('products.newProduct.productCodeHint')}
        error={fieldErrors.productCode}
      >
        <input
          id={`${formId}-code`}
          data-testid="new-product-code"
          type="text"
          value={productCode}
          disabled={submitting}
          aria-invalid={fieldErrors.productCode != null}
          onChange={(event) => {
            const { value } = event.target;
            setProductCode(value);
            revalidate('productCode', validateProductCode(value));
          }}
          className={inputClass}
        />
      </Field>

      <Field
        id={`${formId}-name`}
        label={t('products.newProduct.displayName')}
        error={fieldErrors.displayName}
      >
        <input
          id={`${formId}-name`}
          data-testid="new-product-display-name"
          type="text"
          value={displayName}
          disabled={submitting}
          aria-invalid={fieldErrors.displayName != null}
          onChange={(event) => {
            const { value } = event.target;
            setDisplayName(value);
            revalidate('displayName', validateDisplayName(value));
          }}
          className={inputClass}
        />
      </Field>

      <Field
        id={`${formId}-collateral`}
        label={t('products.newProduct.collateralModel')}
        error={fieldErrors.collateralModel}
      >
        <select
          id={`${formId}-collateral`}
          data-testid="new-product-collateral-model"
          value={collateralModel}
          disabled={submitting}
          aria-invalid={fieldErrors.collateralModel != null}
          onChange={(event) => {
            const value = event.target.value as CollateralModel;
            setCollateralModel(value);
            revalidate('collateralModel', validateCollateralModel(value));
          }}
          className={inputClass}
        >
          <option value="">{t('products.newProduct.choose')}</option>
          {COLLATERAL_MODELS.map((value) => (
            <option key={value} value={value}>
              {t(`products.collateralModel.${value}`)}
            </option>
          ))}
        </select>
      </Field>

      <Field
        id={`${formId}-pricing`}
        label={t('products.newProduct.pricingModel')}
        error={fieldErrors.pricingModel}
      >
        <select
          id={`${formId}-pricing`}
          data-testid="new-product-pricing-model"
          value={pricingModel}
          disabled={submitting}
          aria-invalid={fieldErrors.pricingModel != null}
          onChange={(event) => {
            const value = event.target.value as PricingModel;
            setPricingModel(value);
            revalidate('pricingModel', validatePricingModel(value));
          }}
          className={inputClass}
        >
          <option value="">{t('products.newProduct.choose')}</option>
          {PRICING_MODELS.map((value) => (
            <option key={value} value={value}>
              {t(`products.pricingModel.${value}`)}
            </option>
          ))}
        </select>
      </Field>

      {writeError != null ? <WriteErrorBanner error={writeError} /> : null}

      <div className="flex gap-3">
        <button
          type="submit"
          data-testid="new-product-submit"
          disabled={submitting}
          aria-busy={submitting}
          className="inline-flex items-center rounded-lg bg-gold max-md:min-h-11 px-4 py-1.5 text-body font-medium text-on-gold hover:bg-gold/90 disabled:opacity-60"
        >
          {submitting ? t('products.newProduct.submitting') : t('products.newProduct.submit')}
        </button>
        <button
          type="button"
          data-testid="new-product-cancel"
          disabled={submitting}
          onClick={onCancel}
          className="inline-flex items-center rounded-lg bg-surface-1 px-4 py-1.5 text-body font-medium text-ink-1 ring-1 ring-inset ring-ink-3/20 hover:bg-surface-3 disabled:opacity-60"
        >
          {t('products.newProduct.cancel')}
        </button>
      </div>
    </form>
  );
};
